import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DateRange } from '../../../shared/date-range-picker/date-range';
import * as moment from 'moment';
import { DATETIME_FORMAT } from '../../../shared/utils/timeUtils';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import { getProjectsReportSelector, getReportFiltersSelector } from '../reports.reducer';
import { Project } from '../../shared/models/Project';
import { SetReportFiltersAction } from '../reports.actions';
import { ReportFilters } from './ReportFilters';
import { URLSearchParams } from '@angular/http';
import { ConfigService } from '../../../config/config.service';
import { CrazyCallConfigService } from '../../shared/services/crazycall-config.service';

@Component({
  selector: 'cc-report-filters',
  templateUrl: './report-filters.component.html',
  styleUrls: ['./report-filters.component.scss']
})
export class ReportFiltersComponent implements OnInit {
  public reportsForm: FormGroup;
  public projects: Project[];
  public isExpanded = true;
  public datePickerEndDate: Date;
  public liveModeEnabled = false;

  private readonly backendUrl: string;

  constructor(
    private fb: FormBuilder,
    private store: Store<IState>,
    private crazyCallConfigService: CrazyCallConfigService,
    private configService: ConfigService
  ) {
    this.backendUrl = this.configService.backendUrl;
  }

  ngOnInit() {
    this.createEmptySearchForm();

    this.store.select(getProjectsReportSelector).subscribe((projects: Project[]) => {
      this.projects = projects.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.store.select(getReportFiltersSelector).subscribe((filter: ReportFilters) => {
      if (!filter) {
        return;
      }
      const projects = (filter && filter.projects) || this.projects.map(({ id }) => id);
      this.reportsForm.controls['projects'].setValue(projects);
      this.reportsForm.controls['dateRange'].setValue(filter.dateRange);
      this.liveModeEnabled = filter.liveModeEnabled;
      if (filter.liveModeEnabled) {
        this.datePickerEndDate = filter.dateRange.dateTo;
      }
    });

    if (this.liveModeEnabled) {
      this.setCurrentTimeAsDateTo();
    }
    this.dispatchFiltersToTheStore();
  }

  get dateFrom() {
    return moment(this.reportsForm.controls.dateRange.value.dateFrom).format(DATETIME_FORMAT);
  }

  get dateTo() {
    return moment(this.reportsForm.controls.dateRange.value.dateTo).format(DATETIME_FORMAT);
  }

  get dateRange(): DateRange {
    const { dateFrom, dateTo } = this.reportsForm.controls.dateRange.value;
    return { dateFrom, dateTo };
  }

  get projectsAreSelected() {
    return this.reportsForm.controls.projects.value && this.reportsForm.controls.projects.value.length;
  }

  get selectedProjects() {
    const selectedProjects = this.reportsForm.controls.projects.value;

    return this.projects
      .filter(project => selectedProjects.includes(project.id))
      .map(project => project.name)
      .join(', ');
  }

  get reportUrl(): string {
    const filters = this.reportFilters;
    const params = new URLSearchParams();
    params.set('projectIds', filters.projects.toString());
    params.set('dateFrom', filters.dateRange.dateFrom.toISOString());
    params.set('dateTo', filters.dateRange.dateTo.toISOString());
    params.set('account', this.crazyCallConfigService.appId);
    params.set(
      'utcOffsetInMinutes',
      moment()
        .utcOffset()
        .toString()
    );

    return `${this.backendUrl}/lead-generation/reports?${params.toString()}`;
  }

  selectAllProjects() {
    const projectIds = this.projects.map(project => project.id);
    this.reportsForm.controls['projects'].setValue(projectIds);
  }

  unselectAllProjects() {
    this.reportsForm.controls['projects'].setValue([]);
  }

  createEmptySearchForm() {
    this.reportsForm = this.fb.group({
      dateRange: <DateRange>{
        dateFrom: moment()
          .startOf('day')
          .toDate(),
        dateTo: moment()
          .endOf('day')
          .toDate()
      },
      projects: []
    });
  }

  changeDateRange({ dateFrom, dateTo }) {
    const dateRange: DateRange = {
      dateFrom: moment(dateFrom).toDate(),
      dateTo: moment(dateTo).toDate()
    };
    this.reportsForm.controls['dateRange'].setValue(dateRange);
  }

  setCurrentTimeAsDateTo() {
    const dateRange = { ...this.dateRange, dateTo: moment().toDate() };
    this.reportsForm.controls['dateRange'].setValue(dateRange);
  }

  onSubmit() {
    this.dispatchFiltersToTheStore();
  }

  changeExpandedState() {
    this.isExpanded = !this.isExpanded;
  }

  private dispatchFiltersToTheStore() {
    this.store.dispatch(new SetReportFiltersAction(this.reportFilters));
  }

  private get reportFilters(): ReportFilters {
    const reportFilters: ReportFilters = this.reportsForm.getRawValue();
    reportFilters.dateRange = this.dateRange;
    return reportFilters;
  }
}
